import { useEffect } from 'react';

import { isFileDrag } from 'utils/fileDrag';

/**
 * Swallows file drops that miss a real drop target. The browser's default
 * action for an unhandled drop is to navigate to the dropped file, which
 * replaces the wizard and loses every unsaved answer — so a near-miss on a
 * document slot has to be a no-op rather than a navigation (STP-319).
 */
export function useBlockStrayFileDrops(): void {
  useEffect(() => {
    /* window is the last hop of the bubble path, so a drag a slot already
       claimed arrives here already-cancelled — leave those alone rather than
       overwriting the 'copy' dropEffect the slot set. */
    function swallow(e: globalThis.DragEvent): void {
      if (e.defaultPrevented) return;
      if (!isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      /* Cancelling dragover makes the whole document droppable, so without an
         explicit 'none' the cursor would advertise every square inch of the
         page as a valid target for a file it will silently discard. */
      if (e.type === 'dragover' && e.dataTransfer !== null) {
        e.dataTransfer.dropEffect = 'none';
      }
    }

    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);

    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);
}
